import { useChat, useChatActions, useChatStoreApi } from '@ai-sdk-tools/store';
import type { ChatOnFinishCallback } from 'ai';
import { lastAssistantMessageIsCompleteWithToolCalls } from 'ai';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useAutoResume } from './use-auto-resume';
import { useChatTransport, usePersistedDurableActiveStreamProbe } from './use-chat-transport';
import type { UIMessageWithMetadata } from '../../../lib/types';
import { normalizeChatUuid } from '../lib/notification-helpers';
import { isDurableActiveStreamAlreadyPersisted, isDurableNovaChatCompletionsEndpoint } from '../lib/transport';
import { useNovaContextSafe } from '../providers';

const DURABLE_ACTIVE_STREAM_RECONCILIATION_INTERVAL_MS = 1000;
const MAX_DURABLE_ACTIVE_STREAM_RECONCILIATION_ATTEMPTS = 24;

interface UseNovaChatOptions {
  chatUuid: string;
  initialMessages?: UIMessageWithMetadata[];
  activeStreamId?: string | null;
  /**
   * Gates the auto-resume probe. Pass false while the server snapshot is still
   * loading so reconnection never starts before history has hydrated —
   * snapshots are ignored while a stream is writing, so a probe that wins the
   * race against hydration would leave the chat with only the resumed turn.
   */
  canResume?: boolean;
  onDurablePreview?: (dataPart: unknown) => void;
  onDurableActiveStreamReconciled?: (chatUuid: string) => void;
  onReconnectUnavailable?: (chatUuid: string) => void;
  onFinish?: ChatOnFinishCallback<UIMessageWithMetadata>;
  onError?: (error: Error) => void;
}

/**
 * Wrapper around useChat from @ai-sdk-tools/store with Nova-specific configuration.
 * Configures transport, handles stream resumability, and manages chat state.
 *
 * IMPORTANT: This hook MUST be called before any other components use useChatActions()
 * or other store hooks, as it configures the transport for the store.
 *
 * Persistent data parts are handled through the store. AI SDK delivers transient durable previews through onData
 * without adding them to canonical message parts.
 */
export function useNovaChat({
  chatUuid,
  initialMessages = [],
  activeStreamId,
  canResume = true,
  onDurablePreview,
  onDurableActiveStreamReconciled,
  onReconnectUnavailable,
  onFinish,
  onError,
}: UseNovaChatOptions) {
  const normalizedChatUuid = normalizeChatUuid(chatUuid);
  const chatActivation = useMemo(
    () => ({
      chatUuid: normalizedChatUuid,
      transports: new Set<ReturnType<typeof useChatTransport>>(),
    }),
    [normalizedChatUuid],
  );
  const currentChatActivationRef = useRef(chatActivation);
  useLayoutEffect(() => {
    currentChatActivationRef.current = chatActivation;
  }, [chatActivation]);
  const [attachedWorkflow, setAttachedWorkflow] = useState<{
    activation: typeof chatActivation;
    workflowRunId: string;
  }>();
  const handleWorkflowAttached = useCallback(
    (attachment: { chatUuid: string; workflowRunId: string }) => {
      if (
        currentChatActivationRef.current !== chatActivation ||
        normalizeChatUuid(attachment.chatUuid) !== chatActivation.chatUuid
      ) {
        return;
      }

      setAttachedWorkflow((current) =>
        current?.activation === chatActivation && current.workflowRunId === attachment.workflowRunId
          ? current
          : { activation: chatActivation, workflowRunId: attachment.workflowRunId },
      );
    },
    [chatActivation],
  );
  const transport = useChatTransport({ onReconnectUnavailable, onWorkflowAttached: handleWorkflowAttached });
  chatActivation.transports.add(transport);
  const probePersistedActiveStream = usePersistedDurableActiveStreamProbe();
  const attachedWorkflowRunId =
    attachedWorkflow?.activation === chatActivation ? attachedWorkflow.workflowRunId : undefined;

  const abortActivationReconnects = useCallback(() => {
    for (const activationTransport of chatActivation.transports) {
      activationTransport.abortActiveReconnects?.();
    }
  }, [chatActivation]);

  // AI SDK v7 retains the stream returned by the transport that began a request,
  // even if later context changes create another transport for the same chat.
  // Keep all of those transports alive until this logical chat view is replaced.
  useLayoutEffect(
    () => () => {
      abortActivationReconnects();
    },
    [abortActivationReconnects],
  );

  // Configure the chat store with transport - this must be called first
  const chat = useChat<UIMessageWithMetadata>({
    id: chatUuid,
    resume: false,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    transport, // This configures the store's transport
    messages: initialMessages,
    experimental_throttle: 100,
    onData: onDurablePreview,
    onFinish,
    onError,
  });

  // Get chat actions to access setNewChat and reset
  const chatActions = useChatActions<UIMessageWithMetadata>();
  const chatStoreApi = useChatStoreApi<UIMessageWithMetadata>();
  const stopChat = chat.stop;
  const stop = useCallback(async () => {
    // AI SDK marks a normal active response aborted synchronously. Then abort
    // transport-owned reconnects, which v7 does not connect to Chat.stop().
    const stopped = stopChat();
    abortActivationReconnects();
    return stopped;
  }, [abortActivationReconnects, stopChat]);

  useEffect(() => {
    const installStop = () => {
      if (chatStoreApi.getState().stop !== stop) {
        chatStoreApi.setState({ stop });
      }
    };

    installStop();
    const unsubscribe = chatStoreApi.subscribe((state) => {
      // @ai-sdk-tools/store republishes the underlying AI SDK helpers on each
      // state sync. Reassert the public wrapper without using its private
      // _syncState hook so useChatActions().stop has the same semantics.
      if (state.stop !== stop) {
        chatStoreApi.setState({ stop });
      }
    });

    return () => {
      unsubscribe();
      if (chatStoreApi.getState().stop === stop) {
        chatStoreApi.setState({ stop: stopChat });
      }
    };
  }, [chatStoreApi, stop, stopChat]);

  // Track previous chat UUID and initial messages to detect changes
  const prevChatUuidRef = useRef<string | undefined>(undefined);
  const prevInitialMessagesRef = useRef<UIMessageWithMetadata[] | undefined>(undefined);

  // Hydrate messages from the server snapshot. The local stream state is the
  // source of truth while a request is in flight; server snapshots only apply
  // when they cannot lose information:
  // - Switching chats always hydrates from the snapshot (empty for new chats).
  // - A settled snapshot ending in an assistant message resets the chat to a
  //   clean ready state (also covers restored clarification turns). This
  //   reset never runs while a request is in flight (submitted or streaming),
  //   so a stale assistant-ending refetch can't drop an optimistic message.
  // - While streaming, same-chat snapshot refreshes are ignored entirely —
  //   the snapshot cannot contain the in-progress assistant message yet.
  // - Otherwise the snapshot only applies if it has at least as many messages
  //   as the local state, so a stale refetch can't drop an optimistic message.
  useLayoutEffect(() => {
    const chatUuidChanged = prevChatUuidRef.current !== chatUuid;
    const initialMessagesChanged = prevInitialMessagesRef.current !== initialMessages;

    if (!chatUuidChanged && !initialMessagesChanged) {
      return;
    }

    prevChatUuidRef.current = chatUuid;
    prevInitialMessagesRef.current = initialMessages;

    const setServerBackedMessages = (messages: UIMessageWithMetadata[]) => {
      chatActions.setNewChat(chatUuid, messages);
      // addToolResult reads from the AI SDK chat instance, not just the
      // external store. Keep both in sync for restored clarification turns.
      chat.setMessages(messages);
    };

    if (chatUuidChanged) {
      setServerBackedMessages(initialMessages);
      return;
    }

    const isStreamingLocally = chat.status === 'streaming';
    const isRequestInFlight = isStreamingLocally || chat.status === 'submitted';
    const latestServerMessage = initialMessages.at(-1);

    if (latestServerMessage?.role === 'assistant' && !isRequestInFlight) {
      chatStoreApi.getState().clearTransientDataParts();
      setServerBackedMessages(initialMessages);
      return;
    }

    if (isStreamingLocally) {
      return;
    }

    const localMessages = chatStoreApi.getState().messages ?? [];
    if (initialMessages.length < localMessages.length) {
      return;
    }

    chat.setMessages(initialMessages);
  }, [chatUuid, initialMessages, chat, chatActions, chatStoreApi]);

  // Existing chats probe the stream endpoint on activation. On v1 the backend
  // returns 204 when there is nothing to resume, so the probe is unconditional
  // and the server decides. The durable v2 endpoint instead replays the latest
  // finished turn, so there the probe only runs while the server reports an
  // active stream — otherwise every activation would replay the last turn into
  // the already-hydrated message and duplicate it.
  const apiEndpoint = useNovaContextSafe()?.apiEndpoint;
  // The commit barrier persists the assistant before it clears activeStreamId.
  // During that brief snapshot race, history is already canonical and replaying
  // the same workflow from cursor zero would append duplicate text parts.
  const activeStreamIsAlreadyPersisted = isDurableActiveStreamAlreadyPersisted({
    activeStreamId,
    messages: initialMessages,
  });
  useAutoResume({
    activeStreamId,
    attachedWorkflowRunId,
    autoResume: canResume && !activeStreamIsAlreadyPersisted,
    chatUuid,
    isNewChat: chatUuid.startsWith('new-chat-'),
    requiresActiveStream: isDurableNovaChatCompletionsEndpoint(apiEndpoint),
    resumeStream: chat.resumeStream,
  });

  useEffect(() => {
    if (!activeStreamId || !activeStreamIsAlreadyPersisted || !isDurableNovaChatCompletionsEndpoint(apiEndpoint)) {
      return undefined;
    }

    const abortController = new AbortController();
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const reconcile = async () => {
      attempts += 1;
      try {
        await probePersistedActiveStream({
          chatUuid,
          signal: abortController.signal,
          workflowRunId: activeStreamId,
        });
        if (!abortController.signal.aborted) {
          onDurableActiveStreamReconciled?.(chatUuid);
        }
      } catch (error) {
        if (abortController.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
          return;
        }
      }

      // The first probe can race the short persist-before-finish barrier. Keep
      // probing while the server snapshot still reports this marker so a later
      // terminal/stale state is compare-cleared even if workflow cleanup failed.
      if (!abortController.signal.aborted && attempts < MAX_DURABLE_ACTIVE_STREAM_RECONCILIATION_ATTEMPTS) {
        retryTimeout = setTimeout(() => void reconcile(), DURABLE_ACTIVE_STREAM_RECONCILIATION_INTERVAL_MS);
      }
    };

    void reconcile();

    return () => {
      abortController.abort();
      if (retryTimeout !== undefined) {
        clearTimeout(retryTimeout);
      }
    };
  }, [
    activeStreamId,
    activeStreamIsAlreadyPersisted,
    apiEndpoint,
    chatUuid,
    onDurableActiveStreamReconciled,
    probePersistedActiveStream,
  ]);

  return { ...chat, stop };
}
